import React, { useState, useEffect, useRef } from "react";
import { errorToast } from "../Toasts/Toasts";
import "./Login.css";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { setLogin, homeFor } from "../../redux/features/authSlice.js";
import { requestOtp, verifyOtp, googleConfig, googleSignIn } from "../../services/authService.js";
import { Button } from "react-bootstrap";
import { isPortalHost } from "../../utils/host";

// One page, two ways in: a customer signs in with their phone and a
// WhatsApp code; ManBhawan staff with their company Google account. The role on the
// answer decides where they land.

function finishLogin(dispatch, navigate, response) {
  localStorage.setItem("token", response.token);
  dispatch(setLogin({ user: response.user || null }));
  navigate(homeFor(response.user));
}

const PhoneLogin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const send = async (e) => {
    e.preventDefault();
    if (phone.replace(/\D/g, "").length < 10) return errorToast("Enter your 10-digit mobile number");
    setBusy(true);
    try {
      const r = await requestOtp(phone);
      if (r?.success) setSent(true);
      else errorToast(r?.message || "Could not send the code");
    } catch (err) {
      errorToast(err?.message || "Could not send the code");
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await verifyOtp(phone, code);
      if (r?.success) finishLogin(dispatch, navigate, r);
      else errorToast(r?.message || "Wrong code");
    } catch (err) {
      errorToast(err?.message || "Wrong code");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={sent ? verify : send} className="login-form">
      <div className="form-group">
        <label htmlFor="phone">Mobile number</label>
        <div className="input-group">
          <span className="input-group-text">+91</span>
          <input
            type="tel"
            id="phone"
            inputMode="numeric"
            autoComplete="tel-national"
            className="form-control"
            placeholder="98765 43210"
            value={phone}
            disabled={sent}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
      </div>
      {sent && (
        <div className="form-group">
          <label htmlFor="code">Code from WhatsApp</label>
          <input
            type="text"
            id="code"
            inputMode="numeric"
            autoComplete="one-time-code"
            className="form-control code-input"
            maxLength={6}
            placeholder="6 digits"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            autoFocus
          />
          <small className="text-muted">
            Sent to +91 {phone}.{" "}
            <button type="button" className="btn btn-link btn-sm p-0" onClick={() => { setSent(false); setCode(""); }}>
              Change number
            </button>
          </small>
        </div>
      )}
      <Button type="submit" variant="success" className="w-100" disabled={busy || (sent && code.length !== 6)}>
        {busy ? "Please wait…" : sent ? "Sign in" : "Send code on WhatsApp"}
      </Button>
    </form>
  );
};

// Google Identity Services button for staff. The script loads on demand;
// the client id comes from the server so it is configured in one place.
const GoogleButton = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const holder = useRef(null);
  const [state, setState] = useState("loading"); // loading | ready | off

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let clientId = null;
      try { clientId = (await googleConfig())?.clientId; } catch (e) { /* off */ }
      if (!clientId) return setState("off");
      if (!document.getElementById("gsi")) {
        const sc = document.createElement("script");
        sc.id = "gsi"; sc.src = "https://accounts.google.com/gsi/client"; sc.async = true;
        document.head.appendChild(sc);
        await new Promise((r) => { sc.onload = r; sc.onerror = r; });
      }
      if (cancelled || !window.google?.accounts?.id) return setState("off");
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async ({ credential }) => {
          try {
            const r = await googleSignIn(credential);
            if (r?.success) finishLogin(dispatch, navigate, r);
            else errorToast(r?.message || "Google sign-in failed");
          } catch (err) {
            errorToast(err?.message || "Google sign-in failed");
          }
        },
      });
      if (holder.current) window.google.accounts.id.renderButton(holder.current, { theme: "outline", size: "large", width: 320, text: "signin_with" });
      setState("ready");
    })();
    return () => { cancelled = true; };
  }, [dispatch, navigate]);

  if (state === "off") {
    return (
      <div className="text-center small text-muted py-3">
        Google sign-in is not configured on this deployment, so staff cannot sign in here.
      </div>
    );
  }
  return (
    <div className="mb-3">
      <div ref={holder} className="d-flex justify-content-center" />
      {state !== "ready" && <div className="text-center small text-muted my-2">Loading Google sign-in…</div>}
    </div>
  );
};

// Staff sign in with Google and nothing else (MG, 23 Sep 2026). The
// email/password form is gone from the UI; /api/auth/login still exists, so
// a password account is reachable by API if Google is ever unavailable.
const StaffLogin = () => (
  <div className="login-form">
    <GoogleButton />
    <div className="text-center small text-muted mt-2">ManBhawan staff sign in with their company Google account.</div>
  </div>
);

const Login = () => {
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [mode, setMode] = useState("phone");
  const portal = isPortalHost();
  if (isAuthenticated && user) return <Navigate to={homeFor(user)} replace />;

  // On hortihub.in the staff path is not offered; staff use the CMS host.
  return (
    <div className="login-container">
      <div className="login-card">
        <div className="brand mb-3">
          {portal ? <Link to="/" className="text-decoration-none"><div className="brand-name">HortiHub</div></Link> : <div className="brand-name">HortiHub</div>}
          <div className="brand-by">by ManBhawan Nursery</div>
        </div>
        {!portal && (
          <ul className="nav nav-pills nav-fill mb-3">
            <li className="nav-item">
              <button className={`nav-link ${mode === "phone" ? "active" : ""}`} onClick={() => setMode("phone")}>Customer</button>
            </li>
            <li className="nav-item">
              <button className={`nav-link ${mode === "email" ? "active" : ""}`} onClick={() => setMode("email")}>ManBhawan staff</button>
            </li>
          </ul>
        )}
        {portal || mode === "phone" ? <PhoneLogin /> : <StaffLogin />}
        {portal && <div className="small text-muted text-center mt-3">New to HortiHub? <Link to="/request-access">Request access</Link></div>}
      </div>
    </div>
  );
};

export default Login;
